import { Flex } from "styles/utility-components";

import ReportCollapse from "components/bid_module/common_report_components/ReportCollapse";
import { BuyerActivity } from "components/buyer_activity/BuyerActivity";
import SupplierRelationships from "components/buyer_activity/supplier_relationships/SupplierRelationships";
import { generateContactsUrl } from "components/contacts/utils";
import { SaveContactEntitiesCard } from "components/entities/save_components/SaveContactEntitiesCard";
import { generateNoticeSearchUrl } from "components/notices/utils";
import { RelevanceScoreEnum } from "lib/generated/saved-views/viewConfiguration";
import { LeadDetails, useLeadSupplierBuyerNoticeSummary } from "lib/hooks/api/leads/useLead";
import { EntityActionParams } from "lib/hooks/api/leads/useLeadEntityActions";
import { BuyerActivityObjectType, BuyerActivityStatus } from "lib/types/graphQLEnums";

/**
 * The lead overview as it was before the unified contacts experience: the buyer activity report and relationship
 * history inline, and attached contacts under an "Outreach" collapse.
 *
 * **Delete this file when the unified contacts gate is removed**, along with the `showHeader` prop it
 * relies on — the flag-on layout (`LeadReportsSection` + `LeadContactsSection`) replaces it, with
 * the report moved to `/leads/:id/buyer-activity`.
 */
export function LegacyLeadOverview({
  lead,
  removeEntity,
}: {
  lead: NonNullable<LeadDetails>;
  removeEntity: (entity: EntityActionParams) => void;
}) {
  const noticesSearchUrl = generateNoticeSearchUrl({
    relevanceScore: [RelevanceScoreEnum.Low, RelevanceScoreEnum.Medium, RelevanceScoreEnum.High],
    buyers: lead.buyers?.map((buyer) => buyer.id) ?? [],
  });

  return (
    <Flex column gap={24}>
      {/* Historical buyer activity. `showHeader={false}`: this page has its own title and the
          report's back-link would point at the page it's already on. */}
      <BuyerActivity
        summary={lead.buyerActivity ?? null}
        notices={lead.notices ?? []}
        objectId={lead.id}
        objectType={BuyerActivityObjectType.Lead}
        noticesSearchUrl={noticesSearchUrl}
        showHeader={false}
        buyerName={lead.buyers?.[0]?.name}
        buyerId={lead.buyers?.[0]?.id}
      />
      {/* Relationship history */}
      <SupplierRelationships
        isLoading={lead.buyerActivity?.status === BuyerActivityStatus.Pending}
        savedNotices={lead.notices}
        similarNotices={lead.buyerActivity?.relatedNotices ?? []}
        incumbentSupplierIds={lead.suppliers.map((supplier) => supplier.id)}
        objectId={lead.id}
        queryProvider={useLeadSupplierBuyerNoticeSummary}
        noticesSearchUrl={noticesSearchUrl}
      />
      {/* Outreach */}
      <ReportCollapse title="Outreach">
        <SaveContactEntitiesCard
          contacts={lead.contacts}
          entityUrl={generateContactsUrl({
            buyerGuids: lead.buyers?.map((buyer) => buyer.id) ?? [],
          })}
          removeEntity={removeEntity}
        />
      </ReportCollapse>
    </Flex>
  );
}
